import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { api } from "../lib/api";
import { ArrowLeft, Check, Clock, AlertCircle, RefreshCw, Settings } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function BillingPage() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const refresh = async () => {
    const r = await api.get("/billing/status", { withCredentials: true });
    setStatus(r.data);
  };

  useEffect(() => { refresh(); }, []);

  // Poll on /billing/success?session_id=...
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId || !location.pathname.includes("success")) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/billing/checkout-status/${sessionId}`, { withCredentials: true });
        if (r.data.payment_status === "paid") {
          toast.success("Subscription active — paid days stacked on top of your trial.");
          await refresh();
          if (checkAuth) await checkAuth();
          navigate("/billing", { replace: true });
          return;
        }
        if (attempts < 8) { attempts++; setTimeout(poll, 2000); }
        else toast.error("Payment confirmation timed out — refresh to check status.");
      } catch (e) {
        toast.error("Could not verify payment.");
      }
    };
    poll();
  }, [location, navigate, checkAuth]);

  const openStripeUrl = (url) => {
    // Open in a new tab — Stripe billing/checkout pages set X-Frame-Options: DENY
    // so they cannot load inside the Emergent preview iframe. New-tab works in
    // both preview (iframe) and production (no iframe).
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win || win.closed || typeof win.closed === "undefined") {
      // Pop-up blocked — fall back to top-level navigation
      (window.top || window).location.href = url;
    }
  };

  const subscribe = async (pkg) => {
    setBusy(pkg);
    try {
      const r = await api.post(
        "/billing/checkout",
        { package_id: pkg, origin_url: window.location.origin },
        { withCredentials: true }
      );
      openStripeUrl(r.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await api.get("/billing/portal", {
        params: { origin_url: window.location.origin },
        withCredentials: true,
      });
      openStripeUrl(r.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
    } finally {
      setPortalBusy(false);
    }
  };

  const s = status;
  const subStatus = s?.subscription_status;
  const cancelAtEnd = s?.cancel_at_period_end;
  const hasActiveSub = subStatus === "active" || subStatus === "trialing" || subStatus === "past_due";
  const canManagePortal = hasActiveSub || subStatus === "canceled";
  const planLabel = s?.plan === "yearly" ? "Yearly" : s?.plan === "monthly" ? "Monthly" : "Trial";

  return (
    <div className="App flex flex-col h-screen">
      <Header />
      <main className="flex-1 overflow-y-auto max-w-3xl mx-auto w-full px-6 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-cap">Billing</div>
            <h1 className="text-2xl font-bold tracking-tight mt-1">Your Plan</h1>
          </div>
          <Link to="/" className="btn-outline flex items-center gap-1.5" data-testid="back-to-dashboard-btn">
            <ArrowLeft size={14} /> Dashboard
          </Link>
        </div>

        {s && (
          <div className="panel p-5" data-testid="current-plan">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="label-cap">Current Plan</div>
                <div className="text-3xl font-bold tracking-tight mt-1" data-testid="plan-status-label">
                  {s.status_label}
                </div>
                {hasActiveSub && !cancelAtEnd && (
                  <div className="mt-1 flex items-center gap-1 text-[10px] font-mono text-[#00E676]" data-testid="auto-renew-indicator">
                    <RefreshCw size={10} /> Auto-renews · Cancel anytime
                  </div>
                )}
                {hasActiveSub && cancelAtEnd && (
                  <div className="mt-1 text-[10px] font-mono text-[#FF9F0A]" data-testid="canceling-indicator">
                    Canceling at period end · No further charges
                  </div>
                )}
                {!hasActiveSub && s.plan === "trial" && (
                  <div className="mt-1 text-[10px] font-mono text-[#A1A1AA]" data-testid="trial-indicator">
                    Free trial · {s.days_left} days remaining
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="label-cap">Access until</div>
                <div
                  className={`text-3xl font-mono mt-1 ${s.is_active ? "text-white" : "text-[#FF3B30]"}`}
                  data-testid="days-left"
                >
                  {s.is_active ? `${s.days_left}d` : "0d"}
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-xs text-[#A1A1AA] font-mono">
              <Clock size={11} />
              {s.is_active ? "Expires" : "Expired"}{" "}
              {new Date(s.plan_expires_at).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}
            </div>

            {canManagePortal && (
              <button
                data-testid="manage-subscription-btn"
                onClick={openPortal}
                disabled={portalBusy}
                className="mt-4 btn-outline flex items-center gap-1.5 text-xs"
              >
                <Settings size={12} />
                {portalBusy ? "Opening…" : "Manage subscription / payment method"}
              </button>
            )}

            {!s.is_active && (
              <div className="mt-4 flex items-start gap-2 p-3 border border-[#FF3B30] bg-[rgba(255,59,48,0.06)]">
                <AlertCircle size={14} className="text-[#FF3B30] mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-[#FFD9D6] leading-relaxed">
                  Your plan has expired. You can still view your existing positions, but you cannot
                  add new positions, watchlist tickers, or delete items until you subscribe below.
                </p>
              </div>
            )}
          </div>
        )}

        {!hasActiveSub && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pricing-cards">
            <PlanCard
              id="monthly"
              label="Monthly"
              price="$9.99"
              period="per month"
              features={[
                "Full access to live option chains & roll heatmap",
                "Auto-renews monthly — cancel anytime",
                "Stacks on top of your remaining trial days",
              ]}
              cta={busy === "monthly" ? "Loading…" : "Subscribe Monthly"}
              onBuy={() => subscribe("monthly")}
              disabled={!!busy}
            />
            <PlanCard
              id="yearly"
              label="Yearly · best value"
              highlighted
              price="$99"
              period="per year"
              badge="Save 17%"
              features={[
                "Everything in Monthly, billed once a year",
                "Auto-renews yearly — cancel anytime",
                "Stacks on top of your remaining trial days",
              ]}
              cta={busy === "yearly" ? "Loading…" : "Subscribe Yearly"}
              onBuy={() => subscribe("yearly")}
              disabled={!!busy}
            />
          </div>
        )}

        {hasActiveSub && (
          <div data-testid="switch-plan-section" className="space-y-3">
            <div className="label-cap">Switch plan</div>
            {s?.plan === "monthly" ? (
              <PlanCard
                id="switch-yearly"
                label="Switch to Yearly · best value"
                highlighted
                price="$99"
                period="per year"
                badge="Save 17%"
                features={[
                  "Pay $99 today — stacks +365 days on top of your current balance",
                  "Your monthly subscription stops renewing at its current end date — no double-billing",
                  "Auto-renews yearly thereafter · cancel anytime",
                ]}
                cta={busy === "yearly" ? "Loading…" : "Switch to Yearly"}
                onBuy={() => subscribe("yearly")}
                disabled={!!busy}
              />
            ) : (
              <PlanCard
                id="switch-monthly"
                label="Switch to Monthly"
                price="$9.99"
                period="per month"
                features={[
                  "Pay $9.99 today — stacks +30 days on top of your current balance",
                  "Your yearly subscription stops renewing at its current end date — no double-billing",
                  "Auto-renews monthly thereafter · cancel anytime",
                ]}
                cta={busy === "monthly" ? "Loading…" : "Switch to Monthly"}
                onBuy={() => subscribe("monthly")}
                disabled={!!busy}
              />
            )}
            <p className="text-[10px] text-[#71717A] leading-relaxed">
              You won&rsquo;t lose any time you&rsquo;ve paid for. Stripe will simply stop billing your{" "}
              <span className="font-mono text-white">{s?.plan === "monthly" ? "monthly" : "yearly"}</span>{" "}
              subscription at the end of its current period, and start the new plan immediately.
            </p>
          </div>
        )}

        {hasActiveSub && !cancelAtEnd && (
          <div className="panel p-4" data-testid="active-sub-panel">
            <div className="text-xs text-[#A1A1AA] leading-relaxed">
              Your <span className="text-white font-mono">{planLabel}</span> subscription auto-renews.
              You can update your payment method, switch plans, or cancel at any time from the Stripe billing portal above.
              If you cancel, you keep access until your paid period ends — no early termination charges.
            </div>
          </div>
        )}

        <p className="text-[11px] text-[#71717A] leading-relaxed">
          Subscriptions auto-renew at the rate shown until cancelled. The first payment stacks on top
          of any remaining trial days, so you never lose paid-for time. Manage or cancel anytime via
          the Stripe billing portal — no questions asked.
        </p>
      </main>
    </div>
  );
}

const PlanCard = ({ id, label, price, period, features, cta, onBuy, disabled, highlighted, badge }) => (
  <div
    className={`panel p-5 flex flex-col relative ${highlighted ? "ring-1 ring-[#00E676]" : ""}`}
    data-testid={`plan-card-${id}`}
  >
    {badge && (
      <div className="absolute -top-2 right-3 bg-[#00E676] text-black text-[9px] font-bold px-2 py-0.5 tracking-widest uppercase">
        {badge}
      </div>
    )}
    <div className="label-cap">{label}</div>
    <div className="flex items-baseline gap-2 mt-2">
      <span className="text-4xl font-bold tracking-tight">{price}</span>
      <span className="text-xs text-[#A1A1AA] font-mono">{period}</span>
    </div>
    <ul className="mt-4 space-y-2 text-xs text-[#A1A1AA] flex-1">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-2">
          <Check size={12} className="text-[#00E676] mt-0.5 flex-shrink-0" /> {f}
        </li>
      ))}
    </ul>
    <button
      data-testid={`buy-${id}-btn`}
      onClick={onBuy}
      disabled={disabled}
      className="btn-accent mt-4"
    >
      {cta}
    </button>
  </div>
);
